import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronLeft, CircleUser, Camera, Pencil, CalendarDays, Crown,
  ChevronDown, Gem, Gift, Rocket, Headphones, X, Mail, Phone, Lock, LogOut,
  Sparkles, Star, Zap, Trophy, Heart, Award, Medal, ShieldCheck, BadgeCheck,
  Coins, DollarSign, Clock, Flame, Copy, FileCheck2, Upload, Loader2, CheckCircle2,
} from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { renderInlineMarkdown, parseBenefit } from '../../../lib/inlineMarkdown';
import { tap } from './playerHelpers';
import SafeImage from '../../../components/SafeImage';
import { isPhoneVerified } from '../../../lib/phoneVerification';
import ProfileInstallPanel from './ProfileInstallPanel';
import useIdentityVerification from './useIdentityVerification';
import profileHostLady from '../../../assets/profile-host-lady.png';
import './profile-modern.css';

/* -------------------- BENEFIT ICON SHORTCODES (Iter 145+) --------------------
 * Admins can prefix a benefit with `[shortcode]` (e.g. "[gift] 10% deposit
 * bonus") to render a polished lucide icon instead of an emoji or bullet.
 * Add new mappings here as needed — they're rendered by `BenefitRow` below.
 */
const BENEFIT_ICONS = {
  gift: Gift, sparkles: Sparkles, star: Star, zap: Zap, trophy: Trophy,
  heart: Heart, award: Award, medal: Medal, shield: ShieldCheck, verified: BadgeCheck,
  crown: Crown, gem: Gem, rocket: Rocket, support: Headphones, headphones: Headphones,
  coins: Coins, dollar: DollarSign, money: DollarSign, clock: Clock,
  fast: Zap, priority: Zap, flame: Flame, fire: Flame,
};

const PROFILE_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #22FF88))';
const PROFILE_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 70%, #FFFFFF)';
const PROFILE_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 16%, transparent)';
const PROFILE_ACCENT_SOFT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 24%, transparent)';
const PROFILE_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 46%, transparent)';
const PROFILE_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 42%, transparent)';
const PROFILE_ACCENT_GRADIENT = 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-profile-accent, var(--vl-neon, #22FF88))), var(--vl-btn-primary-to, #00C853))';
const PROFILE_CARD_GRADIENT = 'linear-gradient(135deg, color-mix(in oklab, var(--vl-bg, #000000) 76%, var(--vl-profile-accent, var(--vl-neon, #22FF88))), color-mix(in oklab, var(--vl-bg, #000000) 91%, var(--vl-profile-accent, var(--vl-neon, #22FF88))))';
const PROFILE_GOLD = 'var(--vl-brand-primary, #D4AF37)';
const SHOW_PLAYER_TIER_SYSTEM = false;

const identityToneFor = (status = 'not_submitted') => {
  if (status === 'approved') return { label: 'Verified', color: '#22C55E', bg: 'rgba(34,197,94,0.16)', border: 'rgba(34,197,94,0.32)' };
  if (status === 'pending') return { label: 'Under review', color: '#FACC15', bg: 'rgba(250,204,21,0.14)', border: 'rgba(250,204,21,0.28)' };
  if (status === 'declined') return { label: 'Declined', color: '#FB7185', bg: 'rgba(251,113,133,0.14)', border: 'rgba(251,113,133,0.28)' };
  return { label: 'Required for Send/Receive', color: PROFILE_ACCENT_TEXT, bg: PROFILE_ACCENT_DIM, border: PROFILE_ACCENT_BORDER };
};

const defaultNameFromEmail = (value = '') => {
  const prefix = String(value || '').split('@')[0] || '';
  const first = prefix.match(/[a-z0-9]/i)?.[0] || '';
  return first ? first.toUpperCase() : '';
};

function BenefitRow({ raw, accent = PROFILE_ACCENT }) {
  const { icon, text } = parseBenefit(raw);
  // Resolve a lucide icon when admin used `[shortcode]`.
  let LucideIcon = null;
  if (icon && icon.startsWith('[') && icon.endsWith(']')) {
    LucideIcon = BENEFIT_ICONS[icon.slice(1, -1).toLowerCase()];
  }
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-xl"
      style={{ background: `color-mix(in oklab, ${accent} 10%, transparent)`, border: `1px solid color-mix(in oklab, ${accent} 25%, transparent)` }}
    >
      <span
        className="w-8 h-8 rounded-lg flex items-center justify-center text-base shrink-0"
        style={{ background: `color-mix(in oklab, ${accent} 20%, transparent)`, color: accent }}
        aria-hidden
      >
        {LucideIcon ? <LucideIcon className="w-4 h-4" /> : (icon || '•')}
      </span>
      <span className="text-sm leading-snug" style={{ color: '#E5E7EB' }}>{text}</span>
    </div>
  );
}

/* -------------------- TIER BENEFIT BADGE -------------------- */
function TierBenefit({ icon: Icon, label, color = PROFILE_ACCENT }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid color-mix(in oklab, ${color} 34%, transparent)`, boxShadow: `0 0 14px color-mix(in oklab, ${color} 20%, transparent)` }}
      >
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.2} />
      </span>
      <p className="text-[10px] font-semibold leading-tight text-center" style={{ color: PROFILE_ACCENT_TEXT }}>{label}</p>
    </div>
  );
}

/* -------------------- SETTINGS CARD (used in Security & Account) -------------------- */
function SettingsCard({ icon: Icon, tone = '', title, subtitle, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={() => { tap(); onClick?.(); }}
      className="profile-v2-row"
      data-testid={testid}
    >
      <span className={`profile-v2-row-icon ${tone ? `profile-v2-row-icon--${tone}` : ''}`} aria-hidden>
        <Icon size={22} strokeWidth={1.8} />
      </span>
      <span className="profile-v2-row-copy"><strong>{title}</strong><small>{subtitle}</small></span>
      <ChevronDown className="profile-v2-row-chevron" size={18} aria-hidden />
    </button>
  );
}

function VerifiedUserBadge({ size = 'normal', className = '' }) {
  const dims = size === 'small' ? 'w-4 h-4' : 'w-5 h-5';
  const iconSize = size === 'small' ? 10 : 12;
  return (
    <span
      className={`${dims} rounded-full inline-flex items-center justify-center shrink-0 ${className}`}
      style={{
        background: 'radial-gradient(circle at 30% 25%, #FFF4B8 0%, var(--vl-brand-primary, #D4AF37) 48%, #8B5E00 100%)',
        border: '2px solid #0B0F0C',
        boxShadow: '0 0 8px color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 72%, transparent), inset 0 1px 1px rgba(255,255,255,0.55)',
      }}
      aria-hidden="true"
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/* -------------------- PROFILE FULL-SCREEN -------------------- */
export default function ProfileFullScreen({
  user,
  badge,
  identityFocusKey = 0,
  onClose,
  onEditProfile,
  onEditPhone,
  onResetPassword,
  onContactSupport,
  onOpenAndroidApp,
  showAndroidAppInstall = false,
  showShortcutInstall = true,
  tenantSlug,
  nativeRuntime = false,
  onLogout,
}) {
  const { checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [identityOpen, setIdentityOpen] = useState(Boolean(identityFocusKey));
  const [identityFlowStep, setIdentityFlowStep] = useState('overview');
  const { status: identityStatus, loading: identityLoading, error: identityError, submitting: identitySubmitting, uncertain: identityUncertain, refresh: loadIdentityStatus, submit: submitIdentity } = useIdentityVerification(`${user?.tenant_id || ''}:${user?.id || ''}`);
  const [identityDocumentType, setIdentityDocumentType] = useState('driver_license');
  // Iter 145: admin-edited tier descriptions surfaced when player taps
  // a card in the All-Tiers modal. Map keyed by lowercased tier name.
  const [tierDocs, setTierDocs] = useState({});
  const [tierDetail, setTierDetail] = useState(null); // currently-shown tier in detail modal

  useEffect(() => {
    api.get('/api/referrals/stats')
      .then(r => { if (r.data) setStats(r.data); })
      .catch(() => {});
    if (SHOW_PLAYER_TIER_SYSTEM) {
      // Fetch tier docs (admin-edited descriptions, perks, benefits)
      api.get('/api/tiers')
        .then(r => {
          const list = r.data?.tiers || r.data || [];
          const map = {};
          list.forEach((t) => {
            const k = (t.name || '').toLowerCase();
            if (k) map[k] = t;
          });
          setTierDocs(map);
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    void loadIdentityStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadIdentityStatus, user?.id, user?.tenant_id]);

  useEffect(() => {
    if (!identityFocusKey) return;
    setIdentityOpen(true);
    setIdentityFlowStep('overview');
    void loadIdentityStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityFocusKey]);

  const submitIdentityKyc = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    if (await submitIdentity(fd)) {
      toast.success('Identity verification submitted');
      form.reset();
      setIdentityDocumentType('driver_license');
      setIdentityFlowStep('overview');
    }
  };

  // Removed duplicate useEffect above (keeps single source of truth)
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max image size is 5 MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 320;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.patch('/api/auth/profile', { avatar_url: dataUrl });
      await checkAuth();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally { setUploading(false); }
  };

  const avatarUrl = user?.avatar_url;

  // Tier ladder (display order from bottom -> top)
  const TIERS = [
    { key: 'starter',   name: 'Starter',   level: 1, pointsToNext: 1000,  gradient: 'linear-gradient(135deg, #9CA3AF, #4B5563)', crystal: '#9CA3AF', icon: '🎖️' },
    { key: 'bronze',    name: 'Bronze',    level: 2, pointsToNext: 3000,  gradient: 'linear-gradient(135deg, #CD7F32, #7C4A1E)', crystal: '#CD7F32', icon: '🥉' },
    { key: 'silver',    name: 'Silver',    level: 3, pointsToNext: 5000,  gradient: 'linear-gradient(135deg, #E5E7EB, #9CA3AF)', crystal: '#E5E7EB', icon: '🥈' },
    { key: 'gold',      name: 'Gold',      level: 4, pointsToNext: 10000, gradient: 'linear-gradient(135deg, #FCD34D, #D97706)', crystal: '#FBBF24', icon: '🥇' },
    { key: 'diamond',   name: 'Diamond',   level: 5, pointsToNext: 25000, gradient: 'linear-gradient(135deg, #A78BFA, #6D28D9)', crystal: '#A78BFA', icon: '💎' },
    { key: 'platinum',  name: 'Platinum',  level: 6, pointsToNext: 50000, gradient: 'linear-gradient(135deg, #22D3EE, #0E7490)', crystal: '#22D3EE', icon: '✨' },
    { key: 'elite',     name: 'Elite',     level: 7, pointsToNext: null,  gradient: 'linear-gradient(135deg, #F97316, #DC2626)', crystal: '#F97316', icon: '👑' },
  ];
  const currentTierKey = (user?.achievement_tier || user?.tier || 'starter').toString().toLowerCase();
  const currentTier = TIERS.find(t => t.key === currentTierKey) || TIERS[0];
  const currentPoints = user?.tier_points ?? stats?.tier_points ?? 0;
  const pointsToNext = currentTier.pointsToNext || 10000;
  const tierProgress = Math.min(100, Math.round((currentPoints / pointsToNext) * 100));
  const memberSinceRaw = user?.created_at || user?.member_since;
  const memberSince = memberSinceRaw ? new Date(memberSinceRaw).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;
  const profileName = (user?.full_name || '').trim();
  const defaultProfileName = defaultNameFromEmail(user?.email);
  const visibleProfileName = profileName || defaultProfileName;
  const username = (user?.username || '').trim();
  const usernameLabel = username ? `@${username}` : 'No username set';
  const showVerifiedBadge = !!badge?.show_badge;
  const phoneVerified = isPhoneVerified(user);
  const phoneNumber = user?.phone_e164 || user?.phone_number || user?.phone || '';
  const nameInputRef = useRef(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(visibleProfileName);
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!editingName) setNameDraft(visibleProfileName);
  }, [editingName, visibleProfileName]);

  useEffect(() => {
    if (!editingName) return;
    const t = setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 40);
    return () => clearTimeout(t);
  }, [editingName]);

  const startNameEdit = () => {
    setNameDraft(visibleProfileName);
    setEditingName(true);
  };

  const saveProfileName = async () => {
    if (savingName) return;
    const nextName = (nameDraft || '').trim();
    if (!nextName) {
      setNameDraft(visibleProfileName);
      setEditingName(false);
      return;
    }
    if (nextName === profileName) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await api.patch('/api/auth/profile', { full_name: nextName });
      await checkAuth();
      toast.success('Name updated');
      setEditingName(false);
    } catch (err) {
      setNameDraft(visibleProfileName);
      toast.error(err.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const copyUsername = async () => {
    if (!username) {
      toast.info('Set a username first');
      onEditProfile?.();
      return;
    }
    try {
      await navigator.clipboard.writeText(username);
      toast.success('Username copied');
    } catch {
      toast.error('Could not copy username');
    }
  };

  const showVerifiedUserToast = (e) => {
    e?.stopPropagation?.();
    tap();
    toast.success('Verified user');
  };

  const [viewAllTiers, setViewAllTiers] = useState(false);
  const identityKycStatus = identityStatus?.kyc?.status || user?.kyc_status || (user?.kyc_verified ? 'approved' : 'not_submitted');
  const identityTone = identityToneFor(identityKycStatus);
  const identitySubtitle = 'To help prevent fraud on our site, we collect a photo ID to confirm it is really you.';
  const documentBackRequired = ['driver_license', 'id_card'].includes(identityDocumentType);
  const identityCanSubmit = Boolean(identityStatus) && !identityLoading && !identityUncertain && !['approved', 'pending'].includes(identityKycStatus);
  const identityDocumentTitle = {
    driver_license: 'Driver License',
    passport: 'Passport',
    id_card: 'State ID',
  }[identityDocumentType] || 'Photo ID';
  const openIdentityFlow = () => {
    setIdentityOpen(true);
    setIdentityFlowStep('overview');
    void loadIdentityStatus();
  };
  const avatarInitials = visibleProfileName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('');

  return (
    <div
      className="player-app profile-v2 fixed inset-0 z-[60] flex flex-col overflow-hidden"
      data-testid="profile-fullscreen"
    >
      <header className="profile-v2-topbar"><div className="profile-v2-topbar-inner">
        <button type="button" onClick={onClose} className="profile-v2-back" data-testid="profile-fs-close" aria-label="Back"><ChevronLeft size={21} /></button>
        <h1>My Profile</h1><span aria-hidden="true" />
      </div></header>

      <div className="profile-v2-content" data-testid="profile-scroll-content">
        <section className="profile-v2-summary" aria-label="Profile summary">
          <img src={profileHostLady} alt="" aria-hidden="true" className="profile-v2-host" />
          <div className="profile-v2-summary-body">
            <p className="profile-v2-account-label">Your account</p>
            <div className="profile-v2-identity">
              <div className="profile-v2-avatar-wrap">
                <span className="profile-v2-avatar" aria-label="Your profile photo">
                  {avatarUrl
                    ? <SafeImage src={avatarUrl} alt="" loading="lazy" fallback={avatarInitials || <CircleUser size={27} />} />
                    : avatarInitials || <CircleUser size={27} />}
                </span>
                <button type="button" onClick={() => !uploading && fileInputRef.current?.click()} disabled={uploading} className="profile-v2-camera" data-testid="profile-fs-photo-upload" aria-label="Change photo">
                  {uploading ? <span className="spinner w-4 h-4" /> : <Camera size={15} />}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" data-testid="profile-fs-photo-input" />
              </div>
              <div className="profile-v2-person"><div className="profile-v2-name-line">
                {editingName ? (
                  <input
                    ref={nameInputRef} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} onBlur={saveProfileName}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setNameDraft(profileName); setEditingName(false); } }}
                    maxLength={40} className="profile-v2-name-input" placeholder="Set your name" data-testid="profile-fs-name-input"
                  />
                ) : (
                  <button type="button" onClick={startNameEdit} className="profile-v2-name" data-testid="profile-fs-name" title="Edit name">{visibleProfileName || 'Set your name'}</button>
                )}
                <button type="button" onClick={startNameEdit} className="profile-v2-edit" data-testid="profile-fs-edit-bottom" aria-label="Edit name"><Pencil size={17} /></button>
              </div><p className="profile-v2-person-detail">Player account</p></div>
            </div>
            <div className="profile-v2-meta">
              <button type="button" onClick={copyUsername} className="profile-v2-pill profile-v2-pill--handle" data-testid="profile-fs-username-copy" title={username ? 'Copy username' : 'Set username'}><Copy size={13} /><span>{usernameLabel}</span></button>
              {showVerifiedBadge && <button type="button" onClick={showVerifiedUserToast} className="profile-v2-pill" data-testid="profile-fs-verified-badge" aria-label="Verified user"><VerifiedUserBadge size="small" /> Verified</button>}
              {memberSince && <span className="profile-v2-pill" data-testid="profile-fs-member-since"><CalendarDays size={13} /> Since {memberSince}</span>}
            </div>
          </div>
        </section>

        <div className="profile-v2-section-heading"><h2>Security &amp; Account</h2><span>Keep it current</span></div>
        <section className="profile-v2-list" data-testid="profile-fs-account-actions">
        <SettingsCard
          icon={Mail}
          tone="email"
          title="Email"
          subtitle={user?.email || 'Add your email'}
          onClick={onEditProfile}
          testid="profile-account-email-card"
        />
        <SettingsCard
          icon={Phone}
          tone="phone"
          title="Phone Number"
          subtitle={phoneNumber ? (
            <span className="inline-flex max-w-full items-center gap-1.5">
              <span className="truncate">{phoneNumber}</span>
              {phoneVerified && (
                <CheckCircle2
                  className="h-4 w-4 shrink-0 text-emerald-400"
                  strokeWidth={2.8}
                  aria-label="Phone verified"
                  data-testid="profile-phone-verified-tick"
                />
              )}
            </span>
          ) : 'Add or verify your phone number'}
          onClick={onEditPhone || onEditProfile}
          testid="profile-account-phone-card"
        />
        <SettingsCard
          icon={ShieldCheck}
          tone="identity"
          title="Verify Your Identity"
          subtitle={identitySubtitle}
          onClick={openIdentityFlow}
          testid="profile-account-identity-card"
        />
        </section>
        <ProfileInstallPanel
          tenantSlug={tenantSlug}
          showAndroidAppInstall={showAndroidAppInstall}
          showShortcutInstall={showShortcutInstall}
          nativeRuntime={nativeRuntime}
          onOpenAndroidApp={onOpenAndroidApp}
        />
        <div className="profile-v2-section-heading"><h2>More options</h2></div>
        <section className="profile-v2-list">
        <SettingsCard
          icon={Lock}
          tone="password"
          title="Change Password"
          subtitle="Update your password"
          onClick={onResetPassword}
          testid="profile-account-change-password-card"
        />
        <SettingsCard
          icon={LogOut}
          tone="logout"
          title="Logout"
          subtitle="Sign out of your account"
          onClick={onLogout}
          testid="profile-account-logout-card"
        />
        </section>
      </div>

      {identityOpen && (
        <div
          className="fixed inset-0 z-[95] overflow-y-auto"
          style={{
            background: '#020403',
            color: '#FFFFFF',
            fontFamily: '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
          }}
          data-testid="profile-identity-fullscreen"
        >
          <button
            type="button"
            onClick={() => {
              tap();
              setIdentityOpen(false);
              setIdentityFlowStep('overview');
            }}
            className="fixed top-5 left-5 z-[96] w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95"
            style={{ background: 'rgba(255,255,255,0.06)', color: '#FFFFFF' }}
            data-testid="profile-identity-fullscreen-close"
            aria-label="Close identity verification"
          >
            <X className="w-7 h-7" strokeWidth={2.1} />
          </button>

          <div className="mx-auto flex min-h-full max-w-md flex-col px-4 pb-12 pt-20 sm:px-7">
            <div className="mb-5 flex min-w-0 items-center gap-3">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
                style={{ background: PROFILE_ACCENT_GRADIENT, boxShadow: `0 0 28px ${PROFILE_ACCENT_GLOW}` }}
                aria-hidden
              >
                <ShieldCheck className="w-7 h-7" style={{ color: '#04120A' }} strokeWidth={2.5} />
              </div>
              <h2 className="display min-w-0 text-xl font-black leading-tight sm:text-[1.35rem]">
                Let&apos;s verify your identity
              </h2>
            </div>

            <p className="text-base leading-relaxed mt-1" style={{ color: 'rgba(255,255,255,0.62)' }}>
              To help prevent fraud on our site, we collect a photo ID to confirm it is really you.
            </p>

            <div className="mt-8 space-y-3">
              {identityError && <p role="alert" className="text-sm text-rose-200">{identityError}</p>}
              {identityUncertain && <p role="status" className="text-sm text-amber-200">Your submission may already be saved. Check status before sending it again.</p>}
              <button type="button" onClick={() => void loadIdentityStatus()} disabled={identityLoading || identitySubmitting} className="min-h-11 rounded-xl border border-white/30 px-4 py-2 text-sm font-bold disabled:opacity-60" data-testid="profile-identity-refresh">
                {identityLoading ? 'Checking status…' : 'Check verification status'}
              </button>
              <button
                type="button"
                onClick={() => {
                  tap();
                  if (['pending', 'approved'].includes(identityKycStatus)) { void loadIdentityStatus(); return; }
                  setIdentityFlowStep(identityFlowStep === 'form' ? 'overview' : 'form');
                }}
                className="w-full flex items-center gap-4 rounded-2xl px-1 py-3 text-left transition active:scale-[0.99]"
                aria-expanded={identityFlowStep === 'form' && Boolean(identityStatus) && !['pending', 'approved'].includes(identityKycStatus)}
                data-testid="profile-identity-photo-id-row"
              >
                <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <FileCheck2 className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={2.2} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xl font-extrabold leading-tight">Photo ID</span>
                  <span className="block text-sm mt-1" style={{ color: 'rgba(255,255,255,0.58)' }}>
                    Driver license, passport, or state ID
                  </span>
                  <span
                    className="block text-sm mt-1 font-bold"
                    style={{ color: identityTone.color }}
                  >
                    {identityLoading
                      ? 'Checking status...'
                      : identityKycStatus === 'approved'
                        ? 'Verified'
                        : identityKycStatus === 'pending'
                          ? 'Under review'
                          : 'Need more info to verify'}
                  </span>
                </span>
                {identityLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin shrink-0" style={{ color: 'rgba(255,255,255,0.72)' }} />
                ) : (
                  <ChevronDown
                    className={`w-6 h-6 shrink-0 transition ${identityFlowStep === 'form' ? 'rotate-180' : '-rotate-90'}`}
                    style={{ color: 'rgba(255,255,255,0.68)' }}
                  />
                )}
              </button>

              {identityKycStatus === 'approved' && (
                <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)' }}>
                  <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: '#22C55E' }} />
                  <div>
                    <p className="text-sm font-extrabold">Identity verified</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.58)' }}>
                      Send and receive wallet balance is unlocked for this team.
                    </p>
                  </div>
                </div>
              )}

              {identityKycStatus === 'pending' && (
                <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.28)' }}>
                  <ShieldCheck className="w-6 h-6 shrink-0 mt-0.5" style={{ color: '#FACC15' }} />
                  <div>
                    <p className="text-sm font-extrabold">Under review</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.58)' }}>
                      Our team will check your ID and proceed with your identity verification soon.
                    </p>
                  </div>
                </div>
              )}

              {identityFlowStep === 'form' && identityStatus && !['approved', 'pending'].includes(identityKycStatus) && (
                <form
                  onSubmit={submitIdentityKyc}
                  className="rounded-3xl p-4 space-y-4"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid="profile-identity-fullscreen-form"
                >
                  <fieldset disabled={identitySubmitting || identityLoading || identityUncertain} className="min-w-0 space-y-4">
                  {identityKycStatus === 'declined' && identityStatus?.kyc?.reason && (
                    <div className="rounded-2xl p-3" style={{ background: 'rgba(251,113,133,0.12)', border: '1px solid rgba(251,113,133,0.28)' }}>
                      <p className="text-xs font-bold" style={{ color: '#FDA4AF' }}>
                        Declined reason: {identityStatus.kyc.reason}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-extrabold mb-2" style={{ color: 'rgba(255,255,255,0.48)' }}>
                      Valid ID
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        ['driver_license', 'Driver License', 'Front and back required'],
                        ['passport', 'Passport', 'Photo page required'],
                        ['id_card', 'State ID', 'Front and back required'],
                      ].map(([value, label, helper]) => {
                        const selected = identityDocumentType === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setIdentityDocumentType(value)}
                            className="w-full rounded-2xl px-4 py-3 text-left transition active:scale-[0.99]"
                            style={{
                              background: selected ? PROFILE_ACCENT_DIM : 'rgba(255,255,255,0.05)',
                              border: `1px solid ${selected ? PROFILE_ACCENT_BORDER : 'rgba(255,255,255,0.12)'}`,
                            }}
                          >
                            <span className="flex items-center justify-between gap-3">
                              <span>
                                <span className="block text-sm font-extrabold">{label}</span>
                                <span className="block text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.52)' }}>{helper}</span>
                              </span>
                              {selected && <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: PROFILE_ACCENT_TEXT }} />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <input type="hidden" name="document_type" value={identityDocumentType} />
                  </div>

                  <div className="rounded-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <p className="text-sm font-extrabold">{identityDocumentTitle}</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.52)' }}>
                      Upload a clear JPEG, PNG, WebP, HEIC/HEIF or PDF, up to 8 MB per file. Details must be readable.
                    </p>
                  </div>

                  {[
                    ['identity_photo', 'Your photo / selfie', true],
                    ['document_front', 'Document front', true],
                    ['document_back', 'Document back', documentBackRequired],
                  ].map(([name, label, required]) => (
                    <label
                      key={name}
                      className="block rounded-2xl p-3"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <span className="flex items-center gap-2 text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.72)' }}>
                        <Upload className="w-4 h-4" /> {label}
                        {!required && <span className="font-medium" style={{ color: 'rgba(255,255,255,0.42)' }}>(optional)</span>}
                      </span>
                      <input
                        name={name}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                        required={required}
                        className="block w-full text-xs"
                        style={{ color: 'rgba(255,255,255,0.62)' }}
                      />
                    </label>
                  ))}

                  <textarea
                    name="notes"
                    placeholder="Notes optional"
                    className="min-h-[76px] w-full rounded-2xl px-3 py-2 text-sm outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.12)' }}
                  />

                  <button
                    type="submit"
                    disabled={identitySubmitting || !identityCanSubmit}
                    className="w-full h-12 rounded-2xl display font-extrabold flex items-center justify-center transition active:scale-[0.99] disabled:opacity-70"
                    style={{ background: PROFILE_ACCENT_GRADIENT, color: '#04120A' }}
                    data-testid="profile-identity-submit"
                  >
                    {identitySubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Submit Identity Review
                  </button>
                  </fieldset>
                </form>
              )}
            </div>

            <p className="mt-auto pt-12 text-center text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.46)' }}>
              Learn how verifying your identity helps prevent fraud on our site.{' '}
              <button
                type="button"
                onClick={() => {
                  tap();
                  setIdentityOpen(false);
                  setIdentityFlowStep('overview');
                  onContactSupport?.();
                }}
                className="font-extrabold underline underline-offset-2"
                style={{ color: 'rgba(255,255,255,0.82)' }}
              >
                Contact Support
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Current Tier card */}
      {SHOW_PLAYER_TIER_SYSTEM && (
      <div className="px-4 pb-3 max-w-3xl w-full mx-auto">
        <div
          className="rounded-2xl p-5"
          style={{ background: PROFILE_CARD_GRADIENT, border: `1px solid ${PROFILE_ACCENT_BORDER}`, boxShadow: `0 12px 32px ${PROFILE_ACCENT_GLOW}` }}
          data-testid="profile-fs-tier-card"
        >
          <div className="flex items-center gap-4">
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center shrink-0 relative"
              style={{ background: currentTier.gradient, boxShadow: `0 10px 28px ${currentTier.crystal}77, inset 0 1px 0 rgba(255,255,255,0.25)` }}
            >
              <span className="text-4xl sm:text-5xl" aria-hidden>{currentTier.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>Current Tier</p>
              <h3 className="display text-3xl sm:text-4xl font-extrabold leading-tight" style={{ color: '#fff' }} data-testid="profile-fs-tier-name">{currentTier.name}</h3>
              <div className="mt-1 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}`, color: PROFILE_ACCENT_TEXT }}>
                  <Gem className="w-3 h-3" /> Level {currentTier.level}
                </span>
              </div>
            </div>
            <div className="hidden sm:block text-right shrink-0">
              <p className="display text-2xl font-extrabold" style={{ color: '#fff' }}>
                {currentPoints.toLocaleString()}<span style={{ color: PROFILE_ACCENT_TEXT }}> / </span><span style={{ color: PROFILE_ACCENT_TEXT }}>{pointsToNext.toLocaleString()}</span>
              </p>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>Points to next level</p>
            </div>
          </div>
          <div className="sm:hidden mt-3 text-right">
            <p className="display font-extrabold" style={{ color: '#fff' }}>
              {currentPoints.toLocaleString()}<span style={{ color: PROFILE_ACCENT_TEXT }}> / </span><span style={{ color: PROFILE_ACCENT_TEXT }}>{pointsToNext.toLocaleString()}</span>
            </p>
            <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Points to next level</p>
          </div>
          <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full" style={{ width: `${tierProgress}%`, background: `linear-gradient(90deg, ${PROFILE_GOLD}, ${PROFILE_ACCENT})` }} />
          </div>

          <button
            onClick={() => setViewAllTiers(true)}
            className="mt-4 w-full flex items-center justify-between px-4 py-3 rounded-xl transition active:scale-[0.99] hover:brightness-110"
            style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
            data-testid="profile-fs-view-tiers"
          >
            <span className="flex items-center gap-2">
              <Crown className="w-4 h-4" style={{ color: '#FCD34D' }} />
              <span className="font-bold text-sm">View All Tiers</span>
            </span>
            <ChevronDown className="w-4 h-4 -rotate-90" style={{ color: PROFILE_ACCENT_TEXT }} />
          </button>

          <div className="mt-4 rounded-xl p-3" style={{ background: PROFILE_ACCENT_DIM, border: `1px solid ${PROFILE_ACCENT_BORDER}` }}>
            <p className="text-xs font-bold mb-3" style={{ color: PROFILE_ACCENT_TEXT }}>{currentTier.name} Benefits</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <TierBenefit icon={Gift} label="Higher Rewards" color="#EC4899" />
              <TierBenefit icon={Crown} label="Exclusive Events" color="#FCD34D" />
              <TierBenefit icon={Rocket} label="Faster Withdrawals" color={PROFILE_ACCENT} />
              <TierBenefit icon={Headphones} label="Priority Support" color={PROFILE_ACCENT} />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* View All Tiers modal */}
      {SHOW_PLAYER_TIER_SYSTEM && viewAllTiers && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setViewAllTiers(false)}
          data-testid="profile-fs-tiers-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="display text-xl font-extrabold flex items-center gap-2" style={{ color: '#fff' }}>
                <Crown className="w-5 h-5" style={{ color: '#FCD34D' }} /> All Tiers
              </h3>
              <button onClick={() => setViewAllTiers(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              {[...TIERS].reverse().map((t) => {
                const isCurrent = t.key === currentTier.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      const adminDoc = tierDocs[t.name.toLowerCase()] || null;
                      setTierDetail({ ...t, adminDoc });
                    }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition active:scale-[0.99] hover:brightness-110"
                    style={{
                      background: isCurrent ? PROFILE_ACCENT_DIM : 'rgba(255,255,255,0.03)',
                      border: isCurrent ? `1.5px solid ${PROFILE_ACCENT_BORDER}` : '1px solid rgba(255,255,255,0.06)',
                    }}
                    data-testid={`profile-fs-tier-row-${t.key}`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0" style={{ background: t.gradient }}>{t.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="display font-extrabold text-base" style={{ color: '#fff' }}>{t.name}</p>
                      <p className="text-xs" style={{ color: '#9CA3AF' }}>Level {t.level}{t.pointsToNext ? ` · ${t.pointsToNext.toLocaleString()} XP to unlock` : ' · Top tier'}</p>
                    </div>
                    {isCurrent && (
                      <span className="display text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: '#FCD34D', color: '#3F2A00' }}>Current</span>
                    )}
                    <ChevronDown className="w-4 h-4 -rotate-90 shrink-0 ml-1" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tier detail modal — admin-editable description per tier (Iter 145) */}
      {SHOW_PLAYER_TIER_SYSTEM && tierDetail && (
        <div
          className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center px-4 pb-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
          onClick={() => setTierDetail(null)}
          data-testid="profile-fs-tier-detail"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 max-h-[88vh] overflow-y-auto relative"
            style={{ background: 'var(--vl-bg, #000000)', border: `1px solid ${PROFILE_ACCENT_BORDER}` }}
          >
            <button
              onClick={() => setTierDetail(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }}
              aria-label="Close tier detail"
              data-testid="profile-fs-tier-detail-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center text-center mb-4">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl mb-3"
                style={{ background: tierDetail.gradient, boxShadow: `0 12px 30px ${tierDetail.crystal}55` }}
              >
                {tierDetail.icon}
              </div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                Tier · Level {tierDetail.level}
              </p>
              <h3 className="display text-3xl font-extrabold mt-1" style={{ color: '#fff' }}>
                {tierDetail.name}
              </h3>
              {tierDetail.pointsToNext ? (
                <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
                  {tierDetail.pointsToNext.toLocaleString()} XP to unlock
                </p>
              ) : (
                <p className="text-xs mt-1" style={{ color: '#FCD34D' }}>Top tier</p>
              )}
            </div>

            {/* Admin-edited description (falls back to a friendly default).
                Supports inline markdown: **bold**, *italic*, [text](url),
                and \n linebreaks — rendered safely without dangerouslySetInnerHTML. */}
            <div
              className="text-sm sm:text-base leading-relaxed text-center"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              data-testid="profile-fs-tier-detail-description"
            >
              {tierDetail.adminDoc?.description
                ? renderInlineMarkdown(tierDetail.adminDoc.description)
                : `Reach the ${tierDetail.name} tier to unlock exclusive benefits and rewards. Keep playing to climb higher and earn more.`}
            </div>

            {/* Benefits list — prefer admin-edited benefits; fall back to perks.
                Each benefit is parsed for a leading emoji or [shortcode] icon. */}
            {((tierDetail.adminDoc?.benefits?.length || tierDetail.adminDoc?.perks?.length)) && (
              <div className="mt-5 space-y-2">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: PROFILE_ACCENT_TEXT }}>
                  Benefits at this tier
                </p>
                {(tierDetail.adminDoc?.benefits?.length ? tierDetail.adminDoc.benefits : tierDetail.adminDoc.perks || []).map((b, i) => (
                  <BenefitRow key={i} raw={b} accent={PROFILE_ACCENT} />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setTierDetail(null)}
              className="mt-6 w-full h-11 rounded-2xl display font-extrabold transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: PROFILE_ACCENT_GRADIENT,
                color: 'var(--vl-btn-primary-text, #02140A)',
                boxShadow: `0 10px 24px ${PROFILE_ACCENT_GLOW}`,
              }}
              data-testid="profile-fs-tier-detail-ack"
            >
              Got it
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
